// src/utils/dragDropUtils.js

/**
 * Reorder items within the same list
 * @param {Array} list - The source list
 * @param {number} startIndex - Starting position
 * @param {number} endIndex - Ending position
 * @returns {Array} New array with items reordered
 */
export const reorderList = (list, startIndex, endIndex) => {
    if (!Array.isArray(list)) return [];
    
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
  
    return result;
  };
  
  /**
   * Move an item from one list to another
   * @param {Array} sourceList - Source list
   * @param {Array} destinationList - Destination list
   * @param {number} sourceIndex - Index in source list
   * @param {number} destinationIndex - Index in destination list
   * @returns {Object} Result with updated source and destination lists
   */
  export const moveItemBetweenLists = (
    sourceList,
    destinationList,
    sourceIndex,
    destinationIndex
  ) => {
    if (!Array.isArray(sourceList) || !Array.isArray(destinationList)) {
      return { sourceList: [], destinationList: [] };
    }
    
    const sourceClone = Array.from(sourceList);
    const destClone = Array.from(destinationList);
    const [removed] = sourceClone.splice(sourceIndex, 1);
  
    destClone.splice(destinationIndex, 0, removed);
  
    return {
      sourceList: sourceClone,
      destinationList: destClone
    };
  };
  
  /**
   * Update item order indices after reordering
   * @param {Array} items - Reordered items array
   * @returns {Array} Items with updated orderIndex values
   */
  export const updateOrderIndices = (items) => {
    if (!Array.isArray(items)) return [];
    
    return items.map((item, index) => ({
      ...item,
      orderIndex: index
    }));
  };
  
  /**
   * Extract item IDs from array in correct order
   * @param {Array} items - Array of objects with _id property
   * @returns {Array} Array of ID strings
   */
  export const extractOrderedIds = (items) => {
    if (!Array.isArray(items)) return [];
    
    return items.map(item => item._id).filter(Boolean);
  };
  
  /**
   * Check if drag operation is valid
   * @param {Object} source - Drag source
   * @param {Object} destination - Drag destination
   * @returns {boolean} Whether the operation should proceed
   */
  export const isDragValid = (source, destination) => {
    // Return false if dropped outside a droppable
    if (!destination) return false;
    
    // Return false if dropped in same position
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return false;
    }
    
    return true;
  };
  
  /**
   * Parse section ID from droppable ID
   * @param {string} droppableId - Droppable ID from react-beautiful-dnd
   * @returns {string|null} Actual section ID or null for unsectioned
   */
  export const parseSectionId = (droppableId) => {
    if (!droppableId) return null;
    
    // Handle the special 'no-section' identifier
    if (droppableId === 'no-section') return null;
    
    // If prefixed with 'section-', remove the prefix
    if (droppableId.startsWith('section-')) {
      return droppableId.replace('section-', '');
    }
    
    // Return the droppable ID as-is (this handles the case where section._id is used directly)
    return droppableId;
  };
  
  /**
   * Get droppable ID for section
   * @param {string|null} sectionId - Section ID or null
   * @returns {string} Droppable ID for react-beautiful-dnd
   */
  export const getDroppableId = (sectionId) => {
    if (sectionId === null || sectionId === undefined) {
      return 'no-section';
    }
    
    return `section-${sectionId}`;
  };
  
  /**
   * Get draggable ID for section
   * @param {string|null} sectionId - Section ID or null
   * @returns {string} Draggable ID for react-beautiful-dnd
   */
  export const getSectionDraggableId = (sectionId) => {
    if (sectionId === null || sectionId === undefined) {
      return 'no-section-draggable';
    }
    
    return `section-draggable-${sectionId}`;
  };